import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';
import {
  FaFacebookF,
  FaXTwitter,
  FaYoutube,
  FaInstagram,
  FaLinkedin,
  FaTiktok,
  FaTelegram,
} from 'react-icons/fa6';
import logoLeft from '../assets/logo-iskocya.png';
import logoRight from '../assets/logo-fenerbahce-arma.webp'; // SVG'deki maske telefonda pikselli işleniyordu; aynı tuvalden üretilmiş saydam WebP
import AtaturkSignature from './AtaturkSignature';
import { SOCIAL_LINKS, SOCIAL_ORDER, SOCIAL_LABELS } from '../utils/socialLinks';

const SOCIAL_ICONS = {
  instagram: FaInstagram,
  facebook:  FaFacebookF,
  twitter:   FaXTwitter,
  youtube:   FaYoutube,
  tiktok:    FaTiktok,
  linkedin:  FaLinkedin,
  telegram:  FaTelegram,
};

const Footer = () => {
  const [progress, setProgress] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const flippedThisCycle = useRef(false);
  const rafIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastProgressRef = useRef(0);

  useEffect(() => {
    const DURATION_MS = 2400;
    startTimeRef.current = performance.now();

    const tick = () => {
      const now = performance.now();
      const elapsed = (now - startTimeRef.current) % DURATION_MS;
      const next = (elapsed / DURATION_MS) * 100;

      if (lastProgressRef.current < 50 && next >= 50 && !flippedThisCycle.current) {
        setFlipped(f => !f);
        flippedThisCycle.current = true;
      }
      if (next < lastProgressRef.current) flippedThisCycle.current = false;

      lastProgressRef.current = next;
      setProgress(next);
      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);
    return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
  }, []);

  const showLogoArea = progress >= 48 && progress <= 70;

  const activeSocials = SOCIAL_ORDER.filter((key) => SOCIAL_ICONS[key] && SOCIAL_LINKS[key]);

  return (
    <footer className="footer-container" role="contentinfo" itemScope itemType="https://schema.org/Organization">
      <div className="footer-main">
        {/* Dernek açıklaması paragrafı kullanıcı isteğiyle kaldırıldı (25 Eki 2026).
            Kuruluş adı arama motorları için gizli meta olarak kalır. */}
        <meta itemProp="name" content="İskoçya Fenerbahçeliler Derneği" />

        <nav className="footer-col links-col" aria-label="Footer Navigasyon">
          <div className="link-group">
            <h4>Sayfalar</h4>
            <Link to="/">Anasayfa</Link>
            <Link to="/dernegimiz">Derneğimiz</Link>
            <Link to="/news">Haberler</Link>
            <Link to="/ataturk-fenerbahce">Atatürk &amp; Fenerbahçe</Link>
            <Link to="/store">Mağaza</Link>
          </div>
          <div className="link-group">
            <h4>Üyelik</h4>
            <Link to="/membership">Üyelik Başvurusu</Link>
            <Link to="/dernegimiz#yonetim">Yönetim Kurulu</Link>
            <Link to="/contact">İletişim</Link>
          </div>
        </nav>

        <div className="footer-col quote-col">
          <div className="quote-section">
            <AtaturkSignature />
            <blockquote className="quote-text" cite="https://www.fenerbahce.org">
              <p>
                "Fenerbahçe Kulübü'nün her tarafta takdir edilmiş olan çalışmalarının tesirlerini işitmiş, bu kulübü
                ziyaret ve emeği geçenleri tebrik etmeyi vazife edinmiştim. Bu vazifenin gerçekleşmesi ancak bugün
                nasip olmuştur. Takdir ve tebriklerimi buraya yazmakla övünüyorum."
              </p>
            </blockquote>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="footer-bottom">
        <div className={`footer-logo-container ${flipped ? 'is-flipped' : ''}`}>
          <img src={logoLeft} alt="İskoçya Fenerbahçeliler Derneği Logo" className="footer-logo footer-logo-front" loading="lazy" width="120" height="120" itemProp="logo" />
          <img src={logoRight} alt="Fenerbahçe Spor Kulübü Logo" className="footer-logo footer-logo-back footer-logo-crest" loading="lazy" width="120" height="120" />
        </div>
        {showLogoArea && <div className="footer-progress-line logo-area" />}
        <hr className="footer-divider" />
        <div className="footer-progress-line" style={{ width: `${progress}%` }} />
      </div>

      {/* Social Media Row — at the very bottom */}
      {activeSocials.length > 0 && (
        <div className="footer-social-row">
          <nav className="social-icons" aria-label="Sosyal Medya Bağlantıları">
            {activeSocials.map((key) => {
              const Icon = SOCIAL_ICONS[key];
              return (
                <a
                  key={key}
                  href={SOCIAL_LINKS[key]}
                  aria-label={SOCIAL_LABELS[key]}
                  title={SOCIAL_LABELS[key]}
                  rel="noopener noreferrer"
                  target="_blank"
                  itemProp="sameAs"
                >
                  <Icon />
                </a>
              );
            })}
          </nav>
        </div>
      )}

      <div className="footer-powered-by">
        <p>Powered by <a href="https://bilensoftware.co.uk/" target="_blank" rel="noopener noreferrer" className="footer-link">Bilen Software</a></p>
      </div>
    </footer>
  );
};

export default Footer;
